import { ChangeDetectionStrategy, Component, Inject, Optional } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { first, map } from 'rxjs/operators';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { UiService } from 'src/app/services/ui.service';
import { TranslateService } from '@ngx-translate/core';
import { Frame } from '../../../models/session';
import { WhiteboardBackground } from '../../../services/session-shared-data.service';
import { RealtimeSpaceService } from '../../../services/realtime-space.service';
import { SpacesService } from '../../../services/spaces.service';
import { SpaceRepository } from '../../../state/space.repository';

export interface BoardBackgroundColor {
  name: string;
  color: string;
  label?: string;
}

export interface BoardBackgroundPattern {
  name: string;
  title: string;
  image?: string;
  isBlank?: boolean;
  id: WhiteboardBackground;
}

@Component({
  selector: 'app-wb-change-board-background-modal',
  templateUrl: './wb-change-board-background-modal.component.html',
  styleUrls: ['./wb-change-board-background-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WbChangeBoardBackgroundModalComponent {
  DEFAULT_BACKGROUND_COLOR = '#F6F7F9';
  sessionId = '';
  roomId = '';
  selectedFrame: Frame;
  currentFrameBackgroundPattern: WhiteboardBackground;
  backupFrameBackgroundPattern: WhiteboardBackground;
  currentFrameColor = '';
  backupFrameColor = '';
  currentSupportedBackgroundColors: BoardBackgroundColor[] = [];
  currentSupportedBackgroundPatterns: BoardBackgroundPattern[] = [];
  shouldChangeSpaceDefaultBackground = false;
  showPicker = false;
  isCustomColorSelected = false;

  constructor(
    @Optional() private dialogRef: MatDialogRef<WbChangeBoardBackgroundModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    // private sessionSharedDataService: SessionSharedDataService,
    private realtimeSpaceService: RealtimeSpaceService,
    private spacesService: SpacesService,
    public spaceRepo: SpaceRepository,
    private uiService: UiService,
    private translateService: TranslateService,
  ) {
    this.currentSupportedBackgroundColors = this.getCurrentSupportedBackgroundsColors();
    this.currentSupportedBackgroundPatterns = this.getCurrentSupportedBackgroundsPatterns();
    this.sessionId = data.sessionId;
    this.roomId = data.roomId;
    this.selectedFrame = data.frame;
    this.currentFrameBackgroundPattern =
      this.selectedFrame.backgroundPattern || WhiteboardBackground.DOT;
    this.backupFrameBackgroundPattern = this.currentFrameBackgroundPattern;
    this.currentFrameColor = this.selectedFrame.backgroundColor || this.DEFAULT_BACKGROUND_COLOR;
    this.backupFrameColor = this.currentFrameColor;
    this.isCustomColorSelected = this.checkIfCustomColorSelected();
  }

  isMobile$ = this.uiService.isMobile.asObservable();
  colorPickLeftPosition$ = this.isMobile$.pipe(map((isMobile) => (isMobile ? '25%' : '50%')));

  /**
   * close the modal
   */
  close(): void {
    this.dialogRef.close();
  }

  private getCurrentSupportedBackgroundsPatterns(): BoardBackgroundPattern[] {
    return [
      {
        name: 'DOT_GRID',
        title: this.translateService.instant('Dot Grid'),
        image: 'dot_grid.svg',
        id: WhiteboardBackground.DOT,
      },

      {
        name: 'WRITING_RULE_SMALL',
        title: this.translateService.instant('Writing Rule (Small)'),
        id: WhiteboardBackground.WRITING_RULE_SMALL,
        image: 'writing_rule_small.svg',
      },

      {
        name: 'WRITING_RULE_MEDIUM',
        title: this.translateService.instant('Writing Rule (Medium)'),
        id: WhiteboardBackground.WRITING_RULE_MEDIUM,
        image: 'writing_rule_medium.svg',
      },

      {
        name: 'WRITING_RULE_LARGE',
        title: this.translateService.instant('Writing Rule (Large)'),
        id: WhiteboardBackground.WRITING_RULE_LARGE,
        image: 'writing_rule_large.svg',
      },
      {
        name: 'WIDE_RULE',
        title: this.translateService.instant('Wide Rule'),
        image: 'wide_rule.svg',
        id: WhiteboardBackground.LINE,
      },
      {
        name: 'NARROW_RULE',
        title: this.translateService.instant('Narrow Rule'),
        image: 'narrow_rule.svg',
        id: WhiteboardBackground.NARROW_LINE,
      },
      {
        name: 'LARGE_GRID',
        title: this.translateService.instant('Large Grid'),
        image: 'large_grid.svg',
        id: WhiteboardBackground.SQUARE,
      },
      {
        name: 'SMALL_GRID',
        title: this.translateService.instant('Small Grid'),
        image: 'small_grid.svg',
        id: WhiteboardBackground.SMALL_SQUARE,
      },
      {
        name: 'TRIANGLE',
        title: this.translateService.instant('Triangle'),
        image: 'triangle.svg',
        id: WhiteboardBackground.TRIANGLE,
      },

      {
        name: 'BLANK',
        title: this.translateService.instant('Blank'),
        isBlank: true,
        id: WhiteboardBackground.NONE,
      },
    ];
  }

  private getCurrentSupportedBackgroundsColors(): BoardBackgroundColor[] {
    return [
      {
        name: 'Paper',
        label: this.translateService.instant('Paper'),
        color: this.DEFAULT_BACKGROUND_COLOR,
      },
      {
        name: 'Green',
        label: this.translateService.instant('Green'),
        color: '#ECF7F0',
      },
      {
        name: 'Blue',
        label: this.translateService.instant('Blue'),
        color: '#F0F4FF',
      },
      {
        name: 'Red',
        label: this.translateService.instant('Red'),
        color: '#FCEAEA',
      },
      {
        name: 'Purple',
        label: this.translateService.instant('Purple'),
        color: '#F7EEFB',
      },
      {
        name: 'Yellow',
        label: this.translateService.instant('Yellow'),
        color: '#FFFAE9',
      },
      {
        name: 'Orange',
        label: this.translateService.instant('Orange'),
        color: '#FFECE1',
      },
      {
        name: 'Black',
        label: this.translateService.instant('Black'),
        color: '#1E1E1E',
      },
    ];
  }

  /**
   * check if the confirm button should be enabled
   */
  shouldDisableConfirmButton(): boolean {
    return (
      this.currentFrameBackgroundPattern === this.backupFrameBackgroundPattern &&
      this.currentFrameColor === this.backupFrameColor &&
      !(this.shouldChangeSpaceDefaultBackground && !this.isDefaultBackgroundSelected())
    );
  }

  /**
   * handler to run once user confirm the new pattern
   */
  confirmChangeBackground(): void {
    this.selectedFrame.backgroundPattern = this.currentFrameBackgroundPattern;
    this.selectedFrame.backgroundColor = this.currentFrameColor;
    this.realtimeSpaceService.service.updateFrameBackground(
      this.selectedFrame,
      this.sessionId,
      this.roomId,
    );
    const newDefaultBackground = {
      pattern: this.selectedFrame.backgroundPattern,
      color: this.selectedFrame.backgroundColor,
    };
    // update the space with the new default background if it was changed
    if (this.shouldChangeSpaceDefaultBackground) {
      this.spacesService
        .updateBackgroundDefault(this.sessionId, newDefaultBackground)
        .pipe(first())
        .subscribe((res) => {
          if (res.ok) {
            if (this.spaceRepo.activeSpace) {
              const updatedSpace = this.spaceRepo.activeSpace;
              updatedSpace.sessionDefaultFramesBackground = newDefaultBackground;
              this.spaceRepo.updateSpace(updatedSpace._id, updatedSpace);
            }
          }
        });
    }
    this.close();
  }

  /**
   * once user click on the pattern, this will be the new pattern
   * @param pattern
   */
  changePattern(pattern: BoardBackgroundPattern): void {
    this.currentFrameBackgroundPattern = pattern.id;
  }

  /**
   * once user click on the color, this will be the new background color
   * @param backgroundColor
   */
  changePatternBackgroundColor(backgroundColor: BoardBackgroundColor): void {
    this.currentFrameColor = backgroundColor.color;
    this.isCustomColorSelected = false;
  }

  /**
   * check if the default background pattern is selected
   */
  isDefaultBackgroundSelected(): boolean {
    if (!this.spaceRepo.activeSpace) {
      return false;
    }
    const currentDefaultBackground = this.spaceRepo.activeSpace.sessionDefaultFramesBackground;
    // for spaces without default background just check that it is the blank space
    if (!currentDefaultBackground) {
      return (
        this.currentFrameColor === this.DEFAULT_BACKGROUND_COLOR &&
        this.currentFrameBackgroundPattern === WhiteboardBackground.DOT
      );
    }
    // now we need to check if the current selected pattern equal to the default one in the space
    return (
      this.currentFrameColor === currentDefaultBackground.color &&
      this.currentFrameBackgroundPattern === currentDefaultBackground.pattern
    );
  }

  /**
   * handle the checkbox for default background change
   * @param $event
   */
  handleDefaultBackgroundCheckbox($event: MatCheckboxChange): void {
    this.shouldChangeSpaceDefaultBackground = $event.checked && !this.isDefaultBackgroundSelected();
  }

  /**
   * set the background color with new custom color
   * @param $event
   */
  changePatternWithCustomColor($event: string): void {
    this.currentFrameColor = $event;
    this.showPicker = false;
    this.isCustomColorSelected = true;
  }

  /**
   * check if the current frame color is custom, or it is already predefined
   */
  checkIfCustomColorSelected(): boolean {
    return !this.getCurrentSupportedBackgroundsColors().find(
      (bColor) => bColor.color.toLowerCase() === this.currentFrameColor.toLowerCase(),
    );
  }
}
